<script lang="ts">
  // The app-level line for a take still saving after its page was left
  // (wiki/feature-upload-reliability.md §"Background uploads").
  import { takeUploads, uploadFailureMessage, type TakeUpload } from '$lib/stores/takeUploads.svelte';
  import { Check, X } from '$lib/ui/icons';
  import { t } from '$lib/i18n';
  import { UiKeys } from '@voicedojo/backend-types';
  import { practiceIdFor } from '$lib/theme/practiceThemes';
  import type { Attachment } from 'svelte/attachments';
  import { libraryFocusHref } from '$lib/ui/recording/takeDeepLink';

  // Released on teardown too: a stack that unmounts under the pointer never sees it leave.
  const holdUnderPointer: Attachment<HTMLElement> = (el) => {
    const hold = () => takeUploads.holdNotices();
    const release = () => takeUploads.releaseNotices();
    el.addEventListener('pointerenter', hold);
    el.addEventListener('pointerleave', release);
    return () => {
      el.removeEventListener('pointerenter', hold);
      el.removeEventListener('pointerleave', release);
      release();
    };
  };

  function statusText(upload: TakeUpload): string {
    if (upload.phase === 'failed') return uploadFailureMessage(upload);
    if (upload.phase === 'saved') return t(UiKeys.SaveToLibrary.BackgroundSaved);
    return upload.percent === null
      ? t(UiKeys.SaveToLibrary.BackgroundSaving)
      : t(UiKeys.SaveToLibrary.BackgroundSavingPercent, { Percent: Math.round(upload.percent) });
  }
</script>

{#snippet showInLibrary(upload: TakeUpload)}
  {@const text = statusText(upload)}
  <a
    class="bgu-text"
    href={libraryFocusHref(upload.takeId)}
    aria-label={t(UiKeys.SaveToLibrary.ShowInLibraryAria, { Status: text })}
    data-testid="background-upload-link">{text}</a
  >
{/snippet}

{#if takeUploads.background.length > 0}
  <div
    class="background-uploads no-print"
    role="status"
    aria-live="polite"
    data-testid="background-uploads"
    {@attach holdUnderPointer}
  >
    {#each takeUploads.background as upload (upload.takeId)}
      <div
        class="bgu-item"
        class:failed={upload.phase === 'failed'}
        data-practice={upload.practice ? practiceIdFor(upload.practice) : undefined}
        data-testid="background-upload"
      >
        {#if upload.phase === 'failed'}
          {@render showInLibrary(upload)}
          {#if upload.retryable}
            <button type="button" class="bgu-retry" onclick={() => takeUploads.retry(upload.takeId)}
              >{t(UiKeys.SaveToLibrary.Retry)}</button
            >
          {/if}
          <button
            type="button"
            class="bgu-dismiss"
            aria-label={t(UiKeys.SaveToLibrary.DismissAria)}
            onclick={() => takeUploads.dismiss(upload.takeId)}
          >
            <X size={14} aria-hidden="true" />
          </button>
        {:else}
          {#if upload.phase === 'saved'}<Check size={14} aria-hidden="true" />{/if}
          {@render showInLibrary(upload)}
        {/if}
      </div>
    {/each}
  </div>
{/if}

<style lang="scss">
  /* Pinned to the right edge of the page's content column, not the viewport, so a wide screen keeps it beside the content. */
  .background-uploads {
    position: fixed;
    right: max(1rem, calc((100vw - var(--content-max)) / 2 + var(--page-gutter)));
    bottom: calc(1rem + env(safe-area-inset-bottom, 0px));
    z-index: 40;
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    gap: 0.4rem;
    max-width: min(22rem, calc(100vw - 2rem));
  }

  .bgu-item {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    padding: 0.35rem 0.75rem;
    border: 2px solid var(--color-brand);
    border-radius: 999px;
    background: var(--accent);
    color: var(--on-accent);
    font-family: var(--font-ui);
    font-size: var(--text-meta);
    font-variant-numeric: tabular-nums;
    box-shadow: 0 4px 14px color-mix(in srgb, var(--ink) 18%, transparent);
  }

  .bgu-item.failed {
    border-radius: 0.75rem;
  }

  .bgu-text {
    min-width: 0;
  }

  button {
    flex: none;
    border: 0;
    background: none;
    padding: 0;
    font: inherit;
    cursor: pointer;
  }

  .bgu-retry {
    color: inherit;
    text-decoration: underline;
    text-underline-offset: 2px;
  }

  .bgu-dismiss {
    display: inline-flex;
    color: inherit;
  }
</style>
